/* Reading a recorder's memory card in the browser.
 *
 * A HydroMoth comes back from the water as a card full of folders, and this
 * turns that into objects the receiver can attribute. Three jobs: work out
 * what each file is, work out when it was recorded, and get the bytes to
 * storage without the receiver ever touching them (R7.2).
 *
 * **Why the parsing is here and not on the receiver.** `db/schema.sql` says it
 * outright where `recorder_model` is declared: the model string selects the
 * parser for that make's filenames and header metadata, client-side. The
 * receiver never sees a card's bytes, so it could not read a WAV header even
 * if it wanted to -- the browser is the only thing that holds the file.
 *
 * **What it does NOT do.** It does not build object keys. `app/keys.py` owns
 * the hive layout that DuckDB reads over the bucket, it is contractual, and a
 * second implementation here would be a second thing to keep in step. The
 * browser sends a name and a timestamp and PUTs to whatever it is handed.
 */
import { Md5 } from './md5'

/* ------------------------------------------------------------ filenames */

/** `SENSORNO_YYYYMMDD_HHMMSS.WAV`, and the same without the prefix.
 *
 * Both are real. AudioMoth firmware writes a bare `YYYYMMDD_HHMMSS.WAV`; with
 * a device-id prefix configured it writes the serial in front, which is what
 * makes a card self-describing once it is out of the unit. The prefix is
 * therefore optional here and captured when present, because it is the one
 * chance to notice that this card came out of a different unit than the one
 * the deployment says. */
const WAV_NAME = /^(?:([A-Za-z0-9-]{1,64})_)?(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})\.wav$/i

/** Folders are named `YYYYMMDD`. Used only as a cross-check: the filename
 *  already carries the full instant, so the folder tells us nothing new — but
 *  a file whose folder disagrees with its own name is worth saying out loud
 *  rather than importing quietly. */
const DAY_DIR = /^(\d{4})(\d{2})(\d{2})$/

export interface CardFile {
  file: File
  /** Basename only. `app/keys.py` refuses anything with a path in it, and the
   *  YYYYMMDD folder is not carried into the key — the timestamp partitions
   *  the bucket already, so the folder would be a second copy of the same
   *  fact in a different format. */
  name: string
  folder: string | null
  /** Parsed from the filename, as UTC. */
  startUtc: Date
  /** The serial the file names, when it names one. */
  sensor: string | null
  size: number
  /** Set when the folder's date and the filename's date disagree. */
  folderMismatch: boolean
}

export interface CardScan {
  wavs: CardFile[]
  /** The unit's own configuration file, if it was on the card. */
  config: { file: File; name: string; text: string; parsed: ConfigValues } | null
  /** Files that are on the card but are not recordings and not the config —
   *  reported rather than silently dropped, so "why did it only find 8000 of
   *  my 8400 files" has an answer on screen. */
  skipped: { name: string; why: string }[]
  totalBytes: number
  /** Distinct serials seen across the filenames. More than one means two
   *  units' recordings are mixed on one card, which cannot be attributed to
   *  one deployment. */
  sensors: string[]
}

/** Parse `YYYYMMDD_HHMMSS` as UTC.
 *
 *  `new Date('2026-08-20T15:00:00')` — no trailing Z — is LOCAL time in every
 *  browser, so building the string that way would shift every recording on the
 *  card by the operator's offset, which in Norway is one or two hours
 *  depending on the month. Date.UTC is explicit and cannot drift. */
function utcFrom(y: string, mo: string, d: string, h: string, mi: string, s: string): Date {
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s))
}

/** What one file on the card is, or why it is not a recording. */
export function readName(name: string, folder: string | null): CardFile | { why: string } {
  const m = WAV_NAME.exec(name)
  if (!m) {
    return { why: name.toLowerCase().endsWith('.wav')
      ? 'name is not SENSOR_YYYYMMDD_HHMMSS.wav'
      : 'not a .wav' }
  }
  const [, sensor, y, mo, d, h, mi, s] = m
  const startUtc = utcFrom(y!, mo!, d!, h!, mi!, s!)
  if (Number.isNaN(startUtc.getTime())) return { why: 'unreadable timestamp' }

  const dir = folder ? DAY_DIR.exec(folder) : null
  const folderMismatch = !!dir && (dir[1] !== y || dir[2] !== mo || dir[3] !== d)

  return {
    file: undefined as unknown as File,  // filled in by the caller
    name,
    folder,
    startUtc,
    sensor: sensor ?? null,
    size: 0,
    folderMismatch,
  }
}

/* ------------------------------------------------------- the config file */

export type ConfigValues = Record<string, string>

/** Names a recorder might give its settings file. */
const CONFIG_NAMES = ['config.txt', 'configuration.txt', 'settings.txt']

export function isConfigName(name: string): boolean {
  return CONFIG_NAMES.includes(name.toLowerCase())
}

/** Parse an AudioMoth/HydroMoth `CONFIG.TXT`.
 *
 *  Deliberately structure-agnostic: it takes every `Key : Value` line it finds
 *  and keeps them in order, rather than looking for a known set of fields.
 *
 *  That is not laziness. The file's contents change with firmware — trigger
 *  settings, energy-saver mode and the low-gain range all appeared in later
 *  versions — and a parser that knows the fields would silently drop whatever
 *  it had not been taught, on exactly the deployments where the unusual
 *  setting is the thing worth recording. Everything found is shown to the
 *  operator and the raw file is uploaded alongside, so nothing depends on this
 *  understanding the file. Only a handful of keys are read by name, below, and
 *  each of those is optional.
 */
export function parseConfig(text: string): ConfigValues {
  const out: ConfigValues = {}
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    const at = line.indexOf(':')
    if (at <= 0) continue
    const key = line.slice(0, at).trim()
    const value = line.slice(at + 1).trim()
    // A key is a label, not prose. This rejects the timestamps and sentences
    // some firmware writes as free lines, which also contain a colon.
    if (!key || key.length > 60 || !/^[A-Za-z]/.test(key)) continue
    if (!value) continue
    out[key] = value
  }
  return out
}

/** Read the config's value for any of `names`, case- and spacing-insensitively.
 *  Firmware versions differ on capitalisation and on whether a unit is in the
 *  label, so an exact-match lookup finds nothing on half the cards in a
 *  drawer. */
export function configValue(c: ConfigValues, ...names: string[]): string | null {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const want = names.map(norm)
  for (const [k, v] of Object.entries(c)) {
    if (want.includes(norm(k))) return v
  }
  return null
}

/** The few fields worth acting on, each optional. Everything else in the file
 *  is still shown and still uploaded. */
export function configFacts(c: ConfigValues) {
  return {
    deviceId: configValue(c, 'Device ID', 'DeviceID', 'Serial'),
    firmware: configValue(c, 'Firmware', 'Firmware version'),
    gain: configValue(c, 'Gain'),
    sampleRate: configValue(c, 'Sample rate (Hz)', 'Sample rate', 'Samplerate'),
    timezone: configValue(c, 'Time zone', 'Timezone'),
    recordDuration: configValue(c, 'Recording duration (s)', 'Recording duration'),
    sleepDuration: configValue(c, 'Sleep duration (s)', 'Sleep duration'),
  }
}

/* ------------------------------------------------- walking what was dropped */

/** One entry as the drop handler found it: the file, and the folder it sat in. */
interface Found { file: File; folder: string | null }

/** Recurse a dropped directory.
 *
 *  `readEntries` returns at most 100 entries per call and signals the end with
 *  an empty batch — it does NOT return the whole directory. Calling it once is
 *  the classic bug here and would silently import the first hundred files of
 *  every folder, which looks like it worked. */
function readDir(dir: any, folder: string | null): Promise<Found[]> {
  const reader = dir.createReader()
  const all: Promise<Found[]>[] = []
  return new Promise((resolve, reject) => {
    const step = () => {
      reader.readEntries(async (batch: any[]) => {
        if (batch.length === 0) {
          resolve((await Promise.all(all)).flat())
          return
        }
        for (const e of batch) all.push(walkEntry(e, folder))
        step()
      }, reject)
    }
    step()
  })
}

function walkEntry(entry: any, parent: string | null): Promise<Found[]> {
  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file(
        (f: File) => resolve([{ file: f, folder: parent }]),
        // A file that will not open is skipped rather than failing the drop.
        // One unreadable file on a card must not cost the other eight thousand.
        () => resolve([]),
      )
    })
  }
  // The card's own root is dropped as a directory whose name is the volume
  // label; the folder we care about is the YYYYMMDD one immediately above each
  // file, so the name of the directory being entered becomes the parent.
  return readDir(entry, entry.name ?? parent)
}

/** Everything dropped, flattened. Accepts both routes into the browser: a drag
 *  of folders (`webkitGetAsEntry`) and the folder picker (`webkitRelativePath`
 *  on plain File objects). */
export async function collect(dt: DataTransfer): Promise<Found[]> {
  const entries: any[] = []
  for (const item of Array.from(dt.items)) {
    const e = (item as any).webkitGetAsEntry?.()
    if (e) entries.push(e)
  }
  if (entries.length === 0) {
    // No entries API — fall back to the flat file list, which loses folders
    // but not files. The filename carries the timestamp, so nothing important
    // depends on the folder.
    return Array.from(dt.files).map((f) => ({ file: f, folder: null }))
  }
  const found = await Promise.all(entries.map((e) => walkEntry(e, null)))
  return found.flat()
}

/** The folder picker's files, which carry their path as a string. */
export function fromPicker(files: FileList): Found[] {
  return Array.from(files).map((f) => {
    const rel = (f as any).webkitRelativePath as string | undefined
    const parts = rel ? rel.split('/') : []
    return { file: f, folder: parts.length >= 2 ? parts[parts.length - 2]! : null }
  })
}

/** Turn what was dropped into a scan: recordings, the config, and the rest. */
export async function scan(found: Found[]): Promise<CardScan> {
  const wavs: CardFile[] = []
  const skipped: { name: string; why: string }[] = []
  let config: CardScan['config'] = null
  let totalBytes = 0
  const sensors = new Set<string>()

  for (const { file, folder } of found) {
    const name = file.name
    if (isConfigName(name)) {
      // Only the first one. A card has one unit's settings on it; a second
      // would mean two cards were dropped together, which the sensor check
      // below reports far more clearly than a config collision would.
      if (!config) {
        const text = await file.text()
        config = { file, name, text, parsed: parseConfig(text) }
      }
      continue
    }
    const r = readName(name, folder)
    if ('why' in r) { skipped.push({ name, why: r.why }); continue }
    r.file = file
    r.size = file.size
    totalBytes += file.size
    if (r.sensor) sensors.add(r.sensor)
    wavs.push(r)
  }

  // Oldest first. An import that is interrupted has then covered a contiguous
  // stretch from the start of the deployment rather than a scatter, which is
  // both easier to reason about and more useful if it is never resumed.
  wavs.sort((a, b) => a.startUtc.getTime() - b.startUtc.getTime())

  return { wavs, config, skipped, totalBytes, sensors: [...sensors].sort() }
}

/* -------------------------------------------------------------- uploading */

/** A window of the card that would not come back, after every retry.
 *
 *  Carries where the read stopped, which is the diagnostic question: the same
 *  offset on every attempt is a bad spot in the file, a different offset each
 *  time is the card or the reader rather than the data. */
export class CardReadError extends Error {
  constructor(readonly at: number, readonly total: number, readonly reason: string) {
    super(reason)
    this.name = 'CardReadError'
  }
}

/** How many times one window is asked for before the file is given up on. */
const READ_TRIES = 5

/** Read one window of a file, asking again if the card does not answer.
 *
 *  `slice` is a view, not a read; `arrayBuffer` reads just that window, so the
 *  whole file is never resident -- the point of doing this in windows at all,
 *  since these are gigabyte files and reading one whole needs it in memory
 *  twice.
 *
 *  **Why the retry.** These files live on removable media, and a read off a
 *  card is not the reliable operation that reading a local file is. A reader
 *  that lets the card idle, a hub that resets, a bus that is busy -- any of
 *  them surfaces as `NotReadableError` on one window, and this used to end the
 *  file: a single rejection anywhere in four gigabytes threw away every byte
 *  already hashed. Most of those are a moment rather than a state, and the
 *  window that failed usually comes back on the next ask.
 *
 *  A deliberately unhurried backoff, half a second growing to two: a card that
 *  has gone to sleep needs time to spin up more than it needs to be asked
 *  again quickly, and there is no hurry when the alternative is losing the
 *  file. Five tries is about five seconds before giving up.
 *
 *  What this cannot rescue is an invalidated reference. The browser holds a
 *  snapshot of the file as it was when the folder was dropped, and if the
 *  volume is remounted or the file changes underneath, every read fails from
 *  then on and no number of retries helps -- the folder has to be dropped
 *  again. That is why the error says where it stopped rather than just that it
 *  did.
 */
async function readSlice(file: File, start: number, end: number): Promise<Uint8Array> {
  for (let attempt = 1; ; attempt++) {
    try {
      return new Uint8Array(await file.slice(start, end).arrayBuffer())
    } catch (e) {
      if (attempt >= READ_TRIES) {
        throw new CardReadError(start, file.size,
          e instanceof Error ? e.message : String(e))
      }
      await new Promise((r) => setTimeout(r, 500 * attempt))
    }
  }
}

/** Hash one file. Reads it whole, which bounds memory at one file rather than
 *  one card, and is why hashing happens per batch immediately before the
 *  presign rather than up front for everything. */
export async function hash(
  file: File, onProgress?: (done: number) => void,
): Promise<string> {
  const md5 = new Md5()
  for (let off = 0; off < file.size; off += HASH_CHUNK) {
    const end = Math.min(off + HASH_CHUNK, file.size)
    md5.update(await readSlice(file, off, end))
    onProgress?.(end)
    await breathe()
  }
  return md5.digestBase64()
}

/** One part of a multipart upload, and the two forms of its checksum.
 *
 *  `md5B64` goes in the part's own Content-MD5 so storage rejects a corrupted
 *  part on arrival; `etag` is the same digest as hex, which is what S3 calls
 *  the part's ETag and what the completion has to name. */
export interface Part {
  n: number
  offset: number
  size: number
  md5B64: string
  etag: string
}

/** Hash a large file once, for both the whole object and each of its parts.
 *
 *  One pass. A 4 GiB file read twice — once for the object's checksum and
 *  again for the parts' — is four gigabytes of disk read that buys nothing,
 *  and on a card reader it is minutes. The running digest of the whole file
 *  and the digest of the current part are fed from the same slices.
 *
 *  `onProgress` is called with bytes read, because at this size the checksum
 *  is itself long enough to need a progress bar: nothing has been uploaded
 *  yet, and a still screen at that point is indistinguishable from a hang.
 */
export async function hashParts(
  file: File, partSize: number, onProgress?: (done: number) => void,
): Promise<{ md5B64: string; parts: Part[] }> {
  const whole = new Md5()
  const parts: Part[] = []

  for (let off = 0; off < file.size; off += partSize) {
    const size = Math.min(partSize, file.size - off)
    const part = new Md5()
    for (let at = 0; at < size; at += HASH_CHUNK) {
      const end = Math.min(at + HASH_CHUNK, size)
      const bytes = await readSlice(file, off + at, off + end)
      whole.update(bytes)
      part.update(bytes)
      onProgress?.(off + end)
      await breathe()
    }
    parts.push({ n: parts.length + 1, offset: off, size,
                 md5B64: part.digestBase64(), etag: part.digestHex() })
  }

  return { md5B64: whole.digestBase64(), parts }
}

/** How much of a file is read at a time. Big enough that the per-slice
 *  overhead disappears, small enough that a gigabyte file costs 8 MB of
 *  memory rather than a gigabyte, and that the progress it reports moves
 *  often enough to be watched. */
const HASH_CHUNK = 8 * 1024 * 1024

/** Yield to the event loop. MD5 of a few megabytes is milliseconds of blocking
 *  JavaScript, and a card is thousands of files: without a gap between them
 *  the tab stops painting and the progress bar the operator is watching stops
 *  being a progress bar.
 *
 *  **Not a timer.** This was `setTimeout(r, 0)`, which is the obvious way to
 *  write it and is throttled: a browser clamps timers in a hidden tab to one
 *  per second, and after a few minutes hidden to roughly one per minute. Since
 *  the loop above yields once per 8 MB chunk, that clamp *is* the read rate --
 *  checksumming collapsed to 8 MB/s the moment the operator looked at another
 *  window, so a 4 GB file went from about two minutes to over eight, and to
 *  hours if the tab stayed hidden long enough to earn the harsher clamp. From
 *  the outside it looked like the import had hung partway through a file.
 *
 *  A message posted through a MessageChannel is a macrotask exactly like a
 *  timer -- rendering and input still get their turn between chunks, which is
 *  the whole point of yielding -- but no clamp applies to it. One channel is
 *  shared by every call: ports are cheap, not free, and a card is thousands of
 *  files. Assigning `onmessage` starts the port implicitly. */
const waiting: Array<() => void> = []
const gate = typeof MessageChannel === 'function' ? new MessageChannel() : null
if (gate) gate.port1.onmessage = () => waiting.shift()?.()

export const breathe = () => new Promise<void>((resume) => {
  // No MessageChannel means a runtime that is not a browser -- a test, or a
  // very old engine. Neither is throttled, so the timer is fine there.
  if (!gate) { setTimeout(resume, 0); return }
  waiting.push(resume)
  gate.port2.postMessage(0)
})
